<template>
  <div class="cc-report-container">
    <!-- Main Content Layout -->
    <div class="cc-main-layout" :class="{ 'has-detail-card': showConsultantDetail }">
      <!-- Consultant Detail Card (Left Side) -->
      <div
        v-if="showConsultantDetail"
        class="detail-card-container"
        :style="{ top: `${cardTopPosition}px` }"
      >
        <CCConsultantDetailCard
          :is-open="showConsultantDetail"
          :bee-number="selectedConsultantBeeNumber"
          :consultant-name="selectedConsultantName"
          :status="selectedConsultantStatus"
          :phone="selectedConsultantPhone"
          :email="selectedConsultantEmail"
          :card-top="cardTopPosition"
          :consultant-list="consultantList"
          :report-id="reportId"
          @close="closeConsultantDetail"
          @select-consultant="handleConsultantSelect"
        />
      </div>

      <!-- Report Content (Right Side) -->
      <div class="report-content-wrapper">
        <!-- Filter Flyout -->
    <div class="filter-flyout" :class="{ 'filter-flyout-open': showFilters }">
      <div class="filter-flyout-header">
        <div class="filter-flyout-subheader">
          <h4>{{ t('ccReport.Filters') }}</h4>
          <div>{{ t('ccReport.ApplyFiltersToTableData') }}</div>
        </div>
        <button class="k-button k-flat" @click="showFilters = false">
          <span class="k-icon k-i-close">×</span>
        </button>
      </div>

      <div class="filter-flyout-content">
        <!-- Dynamically generated filters -->
        <div v-if="filterableColumns.length === 0" class="no-filters-message">
          {{ t('ccReport.NoFilterableColumns') }}
        </div>
        <div v-for="column in filterableColumns" :key="column.columnName" class="filter-group">
          <label>{{ column.localizedColumnName }}</label>

          <!-- Date column filter -->
          <div v-if="column.dataType === 'DateDataType'" class="date-filter-container">
            <select
              v-model="dateFilterTypes[column.columnName]"
              class="k-dropdown date-filter-type"
              @change="handleDateFilterTypeChange(column.columnName)"
            >
              <option value="" disabled>{{ t('labels.SelectValue') }}</option>
              <option value="MTD">{{ t('labels.MonthToDate') }}</option>
              <option value="YTD">{{ t('labels.YearToDate') }}</option>
              <option value="Custom">{{ t('labels.CustomRange') }}</option>
            </select>

            <div v-if="dateFilterTypes[column.columnName] === 'Custom'" class="custom-date-range">
              <input
                type="date"
                v-model="customDateRanges[column.columnName].start"
                class="k-textbox date-input"
                :placeholder="t('ccReport.StartDate')"
              />
              <input
                type="date"
                v-model="customDateRanges[column.columnName].end"
                class="k-textbox date-input"
                :placeholder="t('ccReport.EndDate')"
              />
            </div>
          </div>

          <!-- Regular column filter -->
          <select
            v-else
            v-model="pendingFilters[column.columnName]"
            class="k-dropdown"
          >
            <option value="" disabled>{{ t('labels.SelectValue') }}</option>
            <option
              v-for="item in filterOptions[column.columnName]"
              :key="item.value"
              :value="item.value"
            >
              {{ item.text }}
            </option>
          </select>
        </div>
      </div>

      <div class="filter-flyout-footer" v-if="filterableColumns.length > 0">
        <button class="k-button" @click="clearFilters">{{ t('ccReport.ClearFilters') }}</button>
        <button class="k-button k-primary" @click="applyFilters">{{ t('ccReport.ApplyFilters') }}</button>
      </div>
    </div>

        <div class="cc-header">
      <span class="cc-header-title">{{ reportDefinition?.localizedReportName || t('labels.Loading') }}</span>
    </div>

    <div v-if="loading" class="cc-loading">
      <Loader />
    </div>

    <div v-else-if="error" class="cc-error">
      {{ error }}
    </div>

    <div v-else class="cc-report-content">
      <!-- Toolbar with Filter Button -->
      <div class="report-toolbar">
        <button
          v-if="hasHierarchy && !clientFiltersActive"
          class="k-button expand-all-button"
          @click="toggleExpandAll"
        >
          <span class="k-icon" :class="allExpanded ? 'k-i-minus' : 'k-i-plus'"></span>
          {{ allExpanded ? t('ccReport.CollapseAll') : t('ccReport.ExpandAll') }}
        </button>
        <div class="filter-button-container">
          <button class="k-button" @click="showFilters = !showFilters">
            <span class="k-icon k-i-filter"></span>
            {{ t('ccReport.Filters') }}
          </button>
        </div>
      </div>

      <div ref="gridWrapperRef" class="grid-scroll-wrapper">
        <LocalizationProvider :language="normalizedLocale">
          <Grid
            :key="gridKey"
            :data-items="paginatedData"
            :columns="gridColumns"
            :resizable="true"
            :sortable="true"
            :sort="sort"
            :pageable="{
              buttonCount: 5,
              pageSizes: [10, 20, 50, 100],
              info: true
            }"
            :skip="skip"
            :take="pageSize"
            :total="sortedData.length"
            @pagechange="pageChange"
            @sortchange="sortChange"
          />
        </LocalizationProvider>
        <!-- Floating scrollbar: sticks to viewport bottom when grid's native scrollbar is off-screen -->
        <div
          v-show="showFloatingScrollbar"
          ref="floatingScrollbarRef"
          class="floating-scrollbar"
          @scroll="onFloatingScroll"
        >
          <div class="floating-scrollbar-inner" :style="{ width: scrollContentWidth + 'px' }"></div>
        </div>
      </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, onBeforeUnmount, computed, h, watch, nextTick } from 'vue'
import { Grid } from '@progress/kendo-vue-grid'
import type { GridPageChangeEvent, GridSortChangeEvent } from '@progress/kendo-vue-grid'
import { Loader } from '@progress/kendo-vue-indicators'
import { PwsDynamicReportGet, PwsDynamicReportExecute, type DynamicReportDefinition } from '@/api/ConsultantCorner'
import CCConsultantDetailCard from './CCConsultantDetailCard.vue'
import { orderBy } from '@progress/kendo-data-query'
import type { SortDescriptor } from '@progress/kendo-data-query'
import { useI18n } from 'vue-i18n'
import { GetCountryCode } from '@/stores/SettingsStore'
import { loadMessages, LocalizationProvider } from '@progress/kendo-vue-intl'
import { useLogger } from 'vue-logger-plugin'
import { setStoreLogger } from '../../logger.ts'
import { resolveColumnWidth } from '../../utils/kendoHelper.ts'

// Localization
const { t, locale } = useI18n()
const logger = useLogger()
setStoreLogger(logger)

// Normalize locale for Kendo (extract base language: en-US -> en, de-DE -> de)
const normalizedLocale = computed(() => {
  const localeStr = locale.value.toString()
  // Extract base language code (before hyphen if present)
  return localeStr.split('-')[0].toLowerCase()
})

// Country code for date formatting
const countryCode = ref('')
const isEuropeanCountry = computed(() => ['DE', 'AT', 'FR'].includes(countryCode.value))

const loading = ref(true)
const error = ref('')
const skip = ref(0)
const pageSize = ref(50)
const reportDefinition = ref<DynamicReportDefinition | null>(null)
const reportData = ref<any[]>([])
const reportId = ref('')
const expandedRows = ref<Set<any>>(new Set())
const hasHierarchy = ref(false)
const showFilters = ref(false)
const activeFilters = ref<Record<string, any>>({})
const pendingFilters = ref<Record<string, any>>({})
const serverFilters = ref<any[]>([])
const showConsultantDetail = ref(false)
const selectedConsultantBeeNumber = ref<string | null>(null)
const selectedConsultantName = ref('')
const selectedConsultantStatus = ref('')
const selectedConsultantPhone = ref('')
const selectedConsultantEmail = ref('')
const cardTopPosition = ref(0)
const sort = ref<SortDescriptor[]>([])
const dateFilterTypes = ref<Record<string, string>>({})
const customDateRanges = ref<Record<string, { start: string; end: string }>>({})
const gridKey = ref(0)

// Extract consultant list from report data for search functionality
const consultantList = computed(() => {
  const uniqueConsultants = new Map<string, string>() // beeNumber -> name

  reportData.value.forEach(row => {
    const beeNumber = getBeeNumberFromRow(row)
    const name = extractName(row.ConsultantInfo)

    if (beeNumber && name) {
      const beeNumberStr = String(beeNumber)
      if (!uniqueConsultants.has(beeNumberStr)) {
        uniqueConsultants.set(beeNumberStr, name)
      }
    }
  })

  return Array.from(uniqueConsultants.entries()).map(([beeNumber, name]) => ({
    beeNumber,
    name
  }))
})

// Get columns that can be filtered
const filterableColumns = computed(() => {
  if (!reportDefinition.value) {
    console.log('No report definition')
    return []
  }
  console.log('Report columns:', reportDefinition.value.columns)
  const filtered = reportDefinition.value.columns.filter(col => {
    console.log(`Column ${col.columnName}: isFilter=${col.isFilter}`)
    return col.isFilter
  })
  console.log('Filterable columns:', filtered)
  return filtered
})

// Helper function to format dates based on country
function formatDate(dateValue: any): string {
  if (!dateValue) return ''

  const date = new Date(dateValue)
  if (isNaN(date.getTime())) return dateValue

  if (isEuropeanCountry.value) {
    // European format: DD/MM/YYYY
    const day = String(date.getDate()).padStart(2, '0')
    const month = String(date.getMonth() + 1).padStart(2, '0')
    const year = date.getFullYear()
    return `${day}/${month}/${year}`
  } else {
    // Default format
    return date.toLocaleDateString()
  }
}

// Helper function to extract Title from ConsultantInfo (value after hyphen on first line)
function extractTitleFromConsultantInfo(content: string): string | null {
  if (!content || typeof content !== 'string') return null
  const lines = content.split(/\\n|\n|\|\|NL\|\|/).map(line => line.trim()).filter(line => line)
  if (lines.length === 0) return null

  const firstLine = lines[0]
  // Extract value after the last hyphen
  const hyphenIndex = firstLine.lastIndexOf('-')
  if (hyphenIndex === -1) return null

  return firstLine.substring(hyphenIndex + 1).trim()
}

// Helper function to extract BeeNumber from ConsultantInfo
function extractBeeNumberFromConsultantInfo(content: string): number | null {
  if (!content || typeof content !== 'string') return null
  const lines = content.split(/\\n|\n|\|\|NL\|\|/).map(line => line.trim()).filter(line => line)

  for (const line of lines) {
    if (line.includes('Consultant Number:')) {
      const match = line.match(/Consultant Number:\s*(\d+)/)
      if (match && match[1]) {
        return parseInt(match[1], 10)  // Convert to number
      }
    }
  }
  return null
}

// Helper function to get BeeNumber from a row (either from BeeNumber column or ConsultantInfo)
function getBeeNumberFromRow(row: any): any {
  if (row.BeeNumber !== undefined && row.BeeNumber !== null && row.BeeNumber !== '') {
    return row.BeeNumber
  }
  if (row.ConsultantInfo) {
    const extracted = extractBeeNumberFromConsultantInfo(row.ConsultantInfo)
    return extracted  // Already a number
  }
  return null
}

// Helper function to get ParentBeeNumber from a row
function getParentBeeNumberFromRow(row: any): any {
  if (row.ParentBeeNumber !== undefined && row.ParentBeeNumber !== null && row.ParentBeeNumber !== '') {
    return row.ParentBeeNumber
  }
  return null
}

// Get distinct values for each filterable column
const filterOptions = computed(() => {
  const options: Record<string, any[]> = {}

  filterableColumns.value.forEach(column => {
    const columnWithItems = column as any

    // If column has predefined items, use those
    if (columnWithItems.items && columnWithItems.items.length > 0) {
      options[column.columnName] = columnWithItems.items
    } else if (column.columnName === 'ConsultantInfo') {
      // Special handling for ConsultantInfo - extract distinct Titles
      const distinctTitles = new Set<string>()
      reportData.value.forEach(row => {
        const value = row[column.columnName]
        const title = extractTitleFromConsultantInfo(value)
        if (title) {
          distinctTitles.add(title)
        }
      })
      options[column.columnName] = Array.from(distinctTitles).sort().map(title => ({
        value: title,
        text: title
      }))
    } else {
      // Otherwise, extract distinct values from data
      const distinctValues = new Set<any>()
      reportData.value.forEach(row => {
        const value = row[column.columnName]
        if (value !== null && value !== undefined && value !== '') {
          distinctValues.add(value)
        }
      })
      options[column.columnName] = Array.from(distinctValues).sort().map(value => ({
        value: value,
        text: value
      }))
    }
  })

  return options
})

// Apply client-side filters to data before building hierarchy
// Only applies to columns that are in the grid (isFilterOnly: false)
const filteredData = computed(() => {
  let data = reportData.value

  console.log('Starting with data count:', data.length)
  console.log('Active filters:', activeFilters.value)

  // Apply active filters for client-side filtering only
  Object.keys(activeFilters.value).forEach(columnName => {
    const filterValue = activeFilters.value[columnName]
    if (filterValue && filterValue !== '') {
      // Check if this is a client-side filter (column exists in grid)
      const column = reportDefinition.value?.columns.find(col => col.columnName === columnName)
      if (column && !column.isFilterOnly) {
        console.log(`Client-side filtering by ${columnName} = ${filterValue}`)
        const beforeCount = data.length

        // Special handling for ConsultantInfo column - filter by Title
        if (columnName === 'ConsultantInfo') {
          data = data.filter(row => {
            const rowValue = row[columnName]
            const title = extractTitleFromConsultantInfo(rowValue)
            if (!title) return false

            const titleLower = title.toLowerCase()
            const filterValueLower = String(filterValue).toLowerCase()
            return titleLower === filterValueLower
          })
        } else {
          // Regular filtering for other columns
          data = data.filter(row => {
            const rowValue = row[columnName]
            // Case-insensitive comparison
            const rowValueStr = String(rowValue || '').toLowerCase()
            const filterValueStr = String(filterValue).toLowerCase()
            const matches = rowValueStr === filterValueStr
            if (!matches) {
              console.log(`Row filtered out: ${columnName} value="${rowValue}" vs filter="${filterValue}"`)
            }
            return matches
          })
        }
        console.log(`After filtering ${columnName}: ${beforeCount} -> ${data.length} rows`)
      }
    }
  })

  console.log('Final filtered data count:', data.length)
  return data
})

// Apply sorting to filtered data
const sortedData = computed(() => {
  let data = filteredData.value

  // Apply date range filters
  Object.keys(dateFilterTypes.value).forEach(columnName => {
    const filterType = dateFilterTypes.value[columnName]
    if (filterType && filterType !== '') {
      const now = new Date()
      let startDate: Date | null = null
      let endDate: Date | null = null

      if (filterType === 'MTD') {
        // Month to Date
        startDate = new Date(now.getFullYear(), now.getMonth(), 1)
        endDate = now
      } else if (filterType === 'YTD') {
        // Year to Date
        startDate = new Date(now.getFullYear(), 0, 1)
        endDate = now
      } else if (filterType === 'Custom' && customDateRanges.value[columnName]) {
        const range = customDateRanges.value[columnName]
        if (range.start) startDate = new Date(range.start)
        if (range.end) endDate = new Date(range.end)
      }

      if (startDate || endDate) {
        data = data.filter(row => {
          const cellValue = row[columnName]
          if (!cellValue) return false
          const rowDate = new Date(cellValue)
          if (isNaN(rowDate.getTime())) return false

          if (startDate && rowDate < startDate) return false
          if (endDate && rowDate > endDate) return false
          return true
        })
      }
    }
  })

  // Apply sorting if sort descriptors exist
  if (sort.value.length > 0) {
    return orderBy(data, sort.value)
  }

  return data
})

// True when any client-side filter (dropdown or date range) is narrowing the data.
// While one is active we render a flat list instead of a tree — see hierarchicalData.
const clientFiltersActive = computed(() => {
  const hasColumnFilter = Object.keys(activeFilters.value).some(columnName => {
    const filterValue = activeFilters.value[columnName]
    if (!filterValue || filterValue === '') return false
    const column = reportDefinition.value?.columns.find(col => col.columnName === columnName)
    return column && !column.isFilterOnly
  })
  const hasDateFilter = Object.values(dateFilterTypes.value).some(type => type && type !== '')
  return hasColumnFilter || hasDateFilter
})

// Build hierarchical data structure
const hierarchicalData = computed(() => {
  const data = sortedData.value
  // A client-side filter keeps only matching rows in the flat list, pulling them
  // out of their tree context: a match deep in the tree loses its ancestors, and
  // buildHierarchy() can only reach rows by walking DOWN from a root. Any match
  // that isn't itself a root would be orphaned and render nowhere (and if no root
  // matches, the whole grid goes blank). So while a filter is active, show every
  // match as a flat list instead of rebuilding the tree.
  if (!hasHierarchy.value || clientFiltersActive.value || !data.length) {
    return data.map(row => ({ ...row, _isChild: false, _hasChildren: false, _level: 0 }))
  }

  // Build a map of all children for each BeeNumber
  const childrenMap = new Map<any, any[]>()

  data.forEach(row => {
    const parentBeeNumber = getParentBeeNumberFromRow(row)

    if (parentBeeNumber && parentBeeNumber !== '' && parentBeeNumber !== null) {
      if (!childrenMap.has(parentBeeNumber)) {
        childrenMap.set(parentBeeNumber, [])
      }
      childrenMap.get(parentBeeNumber)!.push(row)
    }
  })

  // Recursive function to build hierarchy with levels
  function buildHierarchy(parentBeeNumber: any, level: number): any[] {
    const result: any[] = []

    // Get all rows with this parent (or no parent if it's root level)
    const rows = parentBeeNumber === null
      ? data.filter(row => {
          const parentBee = getParentBeeNumberFromRow(row)
          return !parentBee || parentBee === '' || parentBee === null
        })
      : childrenMap.get(parentBeeNumber) || []

    rows.forEach(row => {
      const rowBeeNumber = getBeeNumberFromRow(row)
      const hasChildren = childrenMap.has(rowBeeNumber)
      const isChild = level > 0

      result.push({
        ...row,
        _isChild: isChild,
        _hasChildren: hasChildren,
        _level: level,
        _beeNumber: rowBeeNumber
      })

      // If this row is expanded and has children, add them recursively
      if (expandedRows.value.has(rowBeeNumber) && hasChildren) {
        result.push(...buildHierarchy(rowBeeNumber, level + 1))
      }
    })

    return result
  }

  return buildHierarchy(null, 0)
})

const paginatedData = computed(() => {
  const data = hierarchicalData.value
  const start = skip.value
  const end = Math.min(start + pageSize.value, data.length)
  return data.slice(start, end)
})

const gridColumns = computed(() => {
  if (!reportDefinition.value) return []

  const columns: any[] = []

  // Add expand/collapse column if hierarchy exists
  if (hasHierarchy.value) {
    columns.push({
      field: '_expand',
      title: '+',
      width: '50px',
      className: 'expand-cell-empty',
      locked: isDesktop.value,
      cell: (h: any, tdElement: any, props: any) => {
        const row = props.dataItem
        // The main tree indentation (level * 20px) lives on the
        // ConsultantInfo/first data column. This expand column is a fixed 50px
        // locked/sticky gutter, so we only apply a tiny per-level nudge here
        // (1px/level) to hint at depth without pushing the +/- glyph outside
        // the column and clipping it.
        const indentPx = (row._level || 0) * 3
        const kendoProps = tdElement?.props || {}
        const kendoClass = kendoProps.class || ''
        const kendoStyle = kendoProps.style || {}

        if (row._hasChildren) {
          // Row with children - show expand/collapse button
          const rowBeeNumber = row._beeNumber || getBeeNumberFromRow(row)
          const isExpanded = expandedRows.value.has(rowBeeNumber)
          return h('td', {
            class: ['expand-cell', kendoClass],
            style: { ...kendoStyle, paddingLeft: `${indentPx}px` },
            onClick: () => toggleExpand(rowBeeNumber)
          }, [
            h('span', {
              class: 'expand-icon',
              innerHTML: isExpanded ? '−' : '+'
            })
          ])
        }

        // Row without children - no toggle
        return h('td', {
          class: [row._isChild ? 'child-row-cell' : '', kendoClass],
          style: { ...kendoStyle, paddingLeft: `${indentPx}px` }
        })
      }
    })
  }

  // Add regular columns
  const visibleColumns = reportDefinition.value.columns
    .sort((a, b) => a.displayOrder - b.displayOrder)
    .filter(column => column.columnName !== 'ParentBeeNumber' && !column.isFilterOnly) // Hide ParentBeeNumber and filter-only columns

  // Calculate total width needed for all columns
  const totalCalculatedWidth = visibleColumns.reduce((sum, col) =>
    sum + resolveColumnWidth(col.minWidth, col.localizedColumnName), 0
  )

  // If total width is less than a reasonable grid width, distribute space
  // Account for expand column if hierarchy exists
  const expandColumnWidth = hasHierarchy.value ? 50 : 0
  const minGridWidth = 1200 // Minimum expected grid width (adjust based on typical container)
  const availableWidth = minGridWidth - expandColumnWidth
  const shouldDistribute = totalCalculatedWidth < availableWidth && visibleColumns.length > 0
  const distributionFactor = shouldDistribute ? availableWidth / totalCalculatedWidth : 1

  const regularColumns = visibleColumns.map((column, index) => {
    let baseWidth = resolveColumnWidth(column.minWidth, column.localizedColumnName)

    // Special handling for ConsultantInfo column - needs more width for emails
    if (column.columnName === 'ConsultantInfo') {
      baseWidth = Math.max(baseWidth, 420) // Ensure minimum width for ConsultantInfo
    }

    const finalWidth = shouldDistribute ? Math.floor(baseWidth * distributionFactor) : baseWidth

    const locked = index === 0 && isDesktop.value
    const headerClasses = locked ? 'centered-header last-sticky' : 'centered-header'
    const cellClasses = locked ? 'centered-cell last-sticky' : 'centered-cell'

    return {
      field: column.columnName,
      title: column.localizedColumnName,
      width: `${finalWidth}px`,
      locked: locked,
      headerClassName: headerClasses,
      className: cellClasses,
      cell: (h: any, tdElement: any, props: any, clickHandler: any) => {
        let content = props.dataItem[column.columnName]
        const row = props.dataItem
        const isFirstColumn = index === 0
        // Preserve Kendo's sticky positioning from tdElement
        const kendoProps = tdElement?.props || {}
        const kendoClass = kendoProps.class || ''
        const kendoStyle = kendoProps.style || {}

        // Format date columns using country-specific format
        if (column.dataType === 'DateDataType' && content) {
          content = formatDate(content)
        }

        // Check if this is the ConsultantInfo column (drill-down)
        if (column.columnName === 'ConsultantInfo') {
          const formattedContent = formatMultilineContent(content)
          const indentPx = hasHierarchy.value && isFirstColumn ? (row._level || 0) * 20 : 0
          const hasIndent = indentPx > 0
          return h('td', {
            innerHTML: formattedContent,
            class: [`multiline-cell consultant-info-cell ${hasIndent ? '' : 'centered-cell'}`, kendoClass],
            style: { ...kendoStyle, paddingLeft: `${indentPx + 12}px`, textAlign: hasIndent ? 'left' : 'center' },
            onClick: (event: MouseEvent) => handleConsultantClick(content, props.dataItem, event)
          })
        }

        if (hasMultilineContent(content)) {
          const formattedContent = formatMultilineContent(content)
          const indentPx = hasHierarchy.value && isFirstColumn ? (row._level || 0) * 20 : 0
          const hasIndent = indentPx > 0
          return h('td', {
            innerHTML: formattedContent,
            class: [`multiline-cell ${hasIndent ? '' : 'centered-cell'}`, kendoClass],
            style: { ...kendoStyle, paddingLeft: `${indentPx + 12}px`, textAlign: hasIndent ? 'left' : 'center' }
          })
        }

        // Apply indentation to first column in hierarchical reports
        const indentPx = hasHierarchy.value && isFirstColumn ? (row._level || 0) * 20 : 0
        const hasIndent = indentPx > 0
        return h('td', {
          class: [hasIndent ? '' : 'centered-cell', kendoClass],
          style: { ...kendoStyle, ...(hasIndent ? { paddingLeft: `${indentPx + 12}px`, textAlign: 'left' } : {}) }
        }, content)
      }
    }
  })

  return [...columns, ...regularColumns]
})

const pageChange = (event: GridPageChangeEvent) => {
  skip.value = event.page.skip
  pageSize.value = event.page.take
}

const sortChange = (event: GridSortChangeEvent) => {
  sort.value = event.sort
  skip.value = 0 // Reset to first page when sorting
}

async function loadReport() {
  loading.value = true
  error.value = ''
  try {
    // Load country code for date formatting
    countryCode.value = await GetCountryCode()
    console.log('Country code loaded:', countryCode.value, 'Is European:', isEuropeanCountry.value)

    // Get report ID from URL
    const reportIdParam = decodeURI(new URLSearchParams(window.location.search).get("id") || '')
    if (!reportIdParam) {
      throw new Error(t('errors.noReportIdSpecified'))
    }

    // Get report definition
    const definition = await PwsDynamicReportGet(reportIdParam)
    console.log('Raw API response:', definition)
    console.log('Definition item:', definition.item)
    console.log('Columns from API:', definition.item.columns)
    reportDefinition.value = definition.item
    reportId.value = reportIdParam

    // Execute report with current server filters
    await executeReport(reportIdParam, definition.item)

    // Check if this report has hierarchical data
    hasHierarchy.value = definition.item.columns.some(col =>
      col.columnName === 'ParentBeeNumber'
    )

    // Auto-expand only root-level rows (e.g. the logged-in user) so their
    // direct downline is visible; deeper rows stay collapsed
    if (hasHierarchy.value) {
      expandRootRows()
    }

    // Initialize date filters
    initializeDateFilters()
  } catch (err: any) {
    error.value = err.message || 'Failed to load report'
    console.error('Error loading report:', err)
  } finally {
    loading.value = false
  }
}

async function executeReport(reportId: string, definition: any) {
  // Exclude columns with isFilterOnly = true
  const columnsToFetch = definition.columns
    .filter((col: any) => !col.isFilterOnly)
    .map((col: any) => col.columnName)

  // Start with base filters from definition
  let allFilters = [...(definition.filters || [])]

  // Replace or add server-side filters
  serverFilters.value.forEach(serverFilter => {
    // Remove any existing filter for this column
    allFilters = allFilters.filter((f: any) => f.columnName !== serverFilter.columnName)
    // Add the new filter
    allFilters.push(serverFilter)
  })

  console.log('Executing report with filters:', allFilters)

  const result = await PwsDynamicReportExecute({
    reportId,
    ColumnNames: columnsToFetch,
    Filters: allFilters
  })

  console.log('Report data:', result.Items)
  reportData.value = result.Items || []
}

// Watch for filter panel opening to sync pending filters with active filters
watch(showFilters, (isOpen) => {
  if (isOpen) {
    // Initialize pending filters with empty strings for all filterable columns
    const initialFilters: Record<string, any> = {}
    filterableColumns.value.forEach(column => {
      // Check if this is a server-side filter (isFilterOnly)
      if (column.isFilterOnly) {
        // Look for the value in serverFilters
        const serverFilter = serverFilters.value.find(f => f.columnName === column.columnName)
        initialFilters[column.columnName] = serverFilter?.value || ''
        console.log(`Server filter ${column.columnName}:`, serverFilter?.value, 'Options:', filterOptions.value[column.columnName])
      } else {
        // Use active filter value for client-side filters
        initialFilters[column.columnName] = activeFilters.value[column.columnName] || ''
      }
    })
    pendingFilters.value = initialFilters
    console.log('Pending filters initialized:', pendingFilters.value)
  }
})

// Initialize custom date ranges for date columns
function initializeDateFilters() {
  if (!reportDefinition.value) return

  reportDefinition.value.columns.forEach(column => {
    if (column.dataType === 'DateDataType' && column.isFilter) {
      if (!customDateRanges.value[column.columnName]) {
        customDateRanges.value[column.columnName] = { start: '', end: '' }
      }
    }
  })
}

function handleDateFilterTypeChange(columnName: string) {
  // Reset custom date range when switching away from Custom
  if (dateFilterTypes.value[columnName] !== 'Custom') {
    customDateRanges.value[columnName] = { start: '', end: '' }
  }
}

// Load Kendo Grid messages based on current locale
function loadKendoMessages() {
  const currentLocale = normalizedLocale.value

  // Wait for next tick to ensure locale change is applied
  setTimeout(() => {
    // Get the raw message strings without vue-i18n processing
    // We need to access the messages directly from the locale files
    const localeMessages: any = {
      'en': {
        itemsPerPage: 'items per page',
        pagerInfo: '{0} - {1} of {2} items',
        noRecords: 'No records available'
      },
      'es': {
        itemsPerPage: 'elementos por página',
        pagerInfo: '{0} - {1} de {2} elementos',
        noRecords: 'No hay registros disponibles'
      },
      'de': {
        itemsPerPage: 'Elemente pro Seite',
        pagerInfo: '{0} - {1} von {2} Elementen',
        noRecords: 'Keine Datensätze verfügbar'
      },
      'fr': {
        itemsPerPage: 'éléments par page',
        pagerInfo: '{0} - {1} sur {2} éléments',
        noRecords: 'Aucun enregistrement disponible'
      }
    }

    const currentMessages = localeMessages[currentLocale] || localeMessages['en']

    // Kendo expects the messages in a specific flat format with dot notation
    const messages = {
      'grid.pagerItemsPerPage': currentMessages.itemsPerPage,
      'grid.pagerInfo': currentMessages.pagerInfo,
      'grid.noRecords': currentMessages.noRecords
    }

    loadMessages(messages, currentLocale)

    // Force grid refresh to apply new messages
    gridKey.value++
  }, 0)
}

// Disable locked columns on mobile viewports where sticky positioning wastes space
const isDesktop = ref(false)
let mediaQuery: MediaQueryList | null = null

function onMediaChange(e: MediaQueryListEvent | MediaQueryList) {
  isDesktop.value = 'matches' in e ? e.matches : false
}

// Floating horizontal scrollbar — sticks to viewport bottom when grid is tall
const gridWrapperRef = ref<HTMLElement | null>(null)
const floatingScrollbarRef = ref<HTMLElement | null>(null)
const showFloatingScrollbar = ref(false)
const scrollContentWidth = ref(0)
let gridContentEl: HTMLElement | null = null
let scrollSyncSource: 'grid' | 'floating' | null = null
let bottomObserver: IntersectionObserver | null = null

function getGridContentEl(): HTMLElement | null {
  if (gridContentEl) return gridContentEl
  if (!gridWrapperRef.value) return null
  gridContentEl = gridWrapperRef.value.querySelector('.k-grid-content') as HTMLElement | null
  return gridContentEl
}

function updateScrollbarWidth() {
  const el = getGridContentEl()
  if (el) {
    scrollContentWidth.value = el.scrollWidth
  }
}

function onGridScroll() {
  if (scrollSyncSource === 'floating') return
  scrollSyncSource = 'grid'
  const el = getGridContentEl()
  if (el && floatingScrollbarRef.value) {
    floatingScrollbarRef.value.scrollLeft = el.scrollLeft
  }
  scrollSyncSource = null
}

function onFloatingScroll() {
  if (scrollSyncSource === 'grid') return
  scrollSyncSource = 'floating'
  const el = getGridContentEl()
  if (el && floatingScrollbarRef.value) {
    el.scrollLeft = floatingScrollbarRef.value.scrollLeft
  }
  scrollSyncSource = null
}

function updateScrollbarPosition() {
  const el = getGridContentEl()
  if (!el || !floatingScrollbarRef.value) return
  const rect = el.getBoundingClientRect()
  floatingScrollbarRef.value.style.left = `${rect.left}px`
  floatingScrollbarRef.value.style.width = `${rect.width}px`
}

function initFloatingScrollbar() {
  const el = getGridContentEl()
  if (!el) return

  updateScrollbarWidth()
  updateScrollbarPosition()
  el.addEventListener('scroll', onGridScroll)
  window.addEventListener('resize', updateScrollbarPosition)

  // Show floating scrollbar only when the grid bottom is below viewport
  const pager = gridWrapperRef.value?.querySelector('.k-pager') as HTMLElement | null
  const sentinel = pager || el

  bottomObserver = new IntersectionObserver(
    (entries) => {
      // If sentinel is not fully visible, show the floating scrollbar
      const entry = entries[0]
      const hasHorizontalOverflow = el.scrollWidth > el.clientWidth
      showFloatingScrollbar.value = !entry.isIntersecting && hasHorizontalOverflow
      if (showFloatingScrollbar.value) {
        updateScrollbarPosition()
      }
    },
    { threshold: 1.0 }
  )
  bottomObserver.observe(sentinel)
}

function cleanupFloatingScrollbar() {
  const el = getGridContentEl()
  if (el) {
    el.removeEventListener('scroll', onGridScroll)
  }
  window.removeEventListener('resize', updateScrollbarPosition)
  if (bottomObserver) {
    bottomObserver.disconnect()
    bottomObserver = null
  }
  gridContentEl = null
}

// Initialize the floating scrollbar once data has loaded and the grid is rendered
watch(loading, (newVal, oldVal) => {
  if (oldVal && !newVal) {
    nextTick(() => {
      cleanupFloatingScrollbar()
      initFloatingScrollbar()
    })
  }
})

onMounted(() => {
  loadKendoMessages()
  loadReport()
  mediaQuery = window.matchMedia('(min-width: 768px)')
  onMediaChange(mediaQuery)
  mediaQuery.addEventListener('change', onMediaChange)
})

onBeforeUnmount(() => {
  if (mediaQuery) {
    mediaQuery.removeEventListener('change', onMediaChange)
  }
  cleanupFloatingScrollbar()
})

// Expand only root-level rows (rows with no ParentBeeNumber) so the
// logged-in user's direct downline is shown; nested rows stay collapsed
function expandRootRows() {
  const expanded = new Set(expandedRows.value)
  reportData.value.forEach(row => {
    const parentBeeNumber = getParentBeeNumberFromRow(row)
    if (!parentBeeNumber) {
      const beeNumber = getBeeNumberFromRow(row)
      if (beeNumber !== null && beeNumber !== undefined && beeNumber !== '') {
        expanded.add(beeNumber)
      }
    }
  })
  expandedRows.value = expanded
}

// Toggle expand/collapse for a parent row
function toggleExpand(beeNumber: any) {
  if (expandedRows.value.has(beeNumber)) {
    expandedRows.value.delete(beeNumber)
  } else {
    expandedRows.value.add(beeNumber)
  }
  // Force reactivity
  expandedRows.value = new Set(expandedRows.value)
}

// Every BeeNumber that has at least one child (i.e. every row that renders a
// +/- toggle). Expanding all of these is equivalent to clicking every + sign.
const expandableBeeNumbers = computed<any[]>(() => {
  const parents = new Set<any>()
  reportData.value.forEach(row => {
    const parentBeeNumber = getParentBeeNumberFromRow(row)
    if (parentBeeNumber !== null && parentBeeNumber !== undefined && parentBeeNumber !== '') {
      parents.add(parentBeeNumber)
    }
  })
  return Array.from(parents)
})

// True when every expandable row is currently expanded
const allExpanded = computed(() =>
  expandableBeeNumbers.value.length > 0 &&
  expandableBeeNumbers.value.every(beeNumber => expandedRows.value.has(beeNumber))
)

// Expand every parent row at once (or collapse everything if already expanded)
function toggleExpandAll() {
  expandedRows.value = allExpanded.value
    ? new Set()
    : new Set(expandableBeeNumbers.value)
  skip.value = 0 // Reset to first page; the visible row count changes
}

// Filter functions
function clearFilters() {
  pendingFilters.value = {}
  activeFilters.value = {}
  serverFilters.value = []
  dateFilterTypes.value = {}
  customDateRanges.value = {}
  skip.value = 0 // Reset pagination to first page

  // Reinitialize date filters
  initializeDateFilters()

  // Reload report data without server filters
  const reportId = decodeURI(new URLSearchParams(window.location.search).get("id") || '')
  if (reportId && reportDefinition.value) {
    executeReport(reportId, reportDefinition.value).then(() => {
      // Force grid refresh after data is loaded
      gridKey.value++
    })
  }
}

async function applyFilters() {
  // Separate client-side and server-side filters
  const clientFilters: Record<string, any> = {}
  const newServerFilters: any[] = []

  Object.keys(pendingFilters.value).forEach(columnName => {
    const filterValue = pendingFilters.value[columnName]
    if (filterValue && filterValue !== '') {
      const column = reportDefinition.value?.columns.find(col => col.columnName === columnName)

      if (column) {
        if (column.isFilterOnly) {
          // Server-side filter - add to server filters array
          newServerFilters.push({
            columnName: columnName,
            filterType: 'Equal',
            value: filterValue
          })
        } else {
          // Client-side filter - add to active filters
          clientFilters[columnName] = filterValue
        }
      }
    }
  })

  // Update active filters (client-side)
  activeFilters.value = clientFilters
  skip.value = 0 // Reset pagination to first page

  // Update server filters and reload if they changed
  const serverFiltersChanged = JSON.stringify(serverFilters.value) !== JSON.stringify(newServerFilters)
  serverFilters.value = newServerFilters

  if (serverFiltersChanged) {
    // Reload report data with new server filters
    const reportId = decodeURI(new URLSearchParams(window.location.search).get("id") || '')
    if (reportId && reportDefinition.value) {
      loading.value = true
      try {
        await executeReport(reportId, reportDefinition.value)
        // Force grid refresh after data is loaded
        gridKey.value++
      } catch (err: any) {
        error.value = err.message || 'Failed to apply filters'
        console.error('Error applying filters:', err)
      } finally {
        loading.value = false
      }
    }
  } else {
    // Even if server filters didn't change, force grid refresh for client-side filters
    gridKey.value++
  }

  showFilters.value = false
}

// Helper functions for handling multiline content
function hasMultilineContent(content: any): boolean {
  if (!content || typeof content !== 'string') return false
  return content.includes('\\n') || content.includes('\n') || content.includes('||NL||')
}

function formatMultilineContent(content: string): string {
  if (!content) return ''
  return content
    .replace(/\\n/g, '<br>')
    .replace(/\n/g, '<br>')
    .replace(/\|\|NL\|\|/g, '<br>')
}

// Extract BeeNumber from ConsultantInfo content (second line)
function extractBeeNumber(content: string): string | null {
  if (!content) return null
  // Split by various newline formats
  const lines = content.split(/\\n|\n|\|\|NL\|\|/).map(line => line.trim()).filter(line => line)
  // BeeNumber is on the second line (index 1)
  if (lines.length >= 2) {
    // Extract just the number part if it has "Consultant Number: " prefix
    const beeNumberLine = lines[1]
    const match = beeNumberLine.match(/\d+/)
    return match ? match[0] : beeNumberLine
  }
  return null
}

// Extract phone from ConsultantInfo content (third line)
function extractPhone(content: string): string {
  if (!content) return ''
  const lines = content.split(/\\n|\n|\|\|NL\|\|/).map(line => line.trim())
  return lines[2] || ''
}

// Extract email from ConsultantInfo content (fourth line)
function extractEmail(content: string): string {
  if (!content) return ''
  const lines = content.split(/\\n|\n|\|\|NL\|\|/).map(line => line.trim())
  return lines[3] || ''
}

// Extract name from ConsultantInfo content (first line)
function extractName(content: string): string {
  if (!content) return ''
  const lines = content.split(/\\n|\n|\|\|NL\|\|/).map(line => line.trim()).filter(line => line)
  return lines[0] || ''
}

// Helper function to extract status from row data (supports multiple column names)
function extractStatus(rowData: any): string {
  // Try different possible status column names
  const statusColumns = ['Status', 'ConsultantStatus', 'CurrentStatus']

  for (const columnName of statusColumns) {
    if (rowData[columnName] !== undefined && rowData[columnName] !== null && rowData[columnName] !== '') {
      return String(rowData[columnName])
    }
  }

  return ''
}

// Handle consultant info cell click
function handleConsultantClick(content: string, rowData: any, event: MouseEvent) {
  const beeNumber = extractBeeNumber(content)
  if (!beeNumber) {
    console.error('Could not extract BeeNumber from ConsultantInfo')
    return
  }

  // Calculate card position based on clicked row relative to the report container
  const target = event.currentTarget as HTMLElement
  if (target) {
    const reportContainer = document.querySelector('.cc-main-layout') as HTMLElement
    if (reportContainer) {
      const containerRect = reportContainer.getBoundingClientRect()
      const targetRect = target.getBoundingClientRect()

      // Calculate offset from top of container
      let offsetFromTop = targetRect.top - containerRect.top

      // Ensure the card doesn't go above the container
      offsetFromTop = Math.max(0, offsetFromTop)

      // Estimate card height (approximately 450px) and ensure it doesn't overflow
      const estimatedCardHeight = 450
      const maxOffset = containerRect.height - estimatedCardHeight
      if (maxOffset > 0 && offsetFromTop > maxOffset) {
        offsetFromTop = maxOffset
      }

      cardTopPosition.value = offsetFromTop
    }
  }

  console.log('rowData    ', rowData)
  selectedConsultantBeeNumber.value = beeNumber
  selectedConsultantName.value = extractName(content)
  selectedConsultantStatus.value = extractStatus(rowData)
  selectedConsultantPhone.value = extractPhone(content)
  selectedConsultantEmail.value = extractEmail(content)
  showConsultantDetail.value = true
}

// Close consultant detail panel
function closeConsultantDetail() {
  showConsultantDetail.value = false
}

// Handle consultant selection from search bar
function handleConsultantSelect(beeNumber: string) {
  const consultant = reportData.value.find(row => {
    const rowBeeNumber = getBeeNumberFromRow(row)
    return String(rowBeeNumber) === beeNumber
  })

  if (consultant) {
    selectedConsultantBeeNumber.value = beeNumber
    selectedConsultantName.value = extractName(consultant.ConsultantInfo)
    selectedConsultantStatus.value = extractStatus(consultant)
    selectedConsultantPhone.value = extractPhone(consultant.ConsultantInfo)
    selectedConsultantEmail.value = extractEmail(consultant.ConsultantInfo)
  }
}
</script>

<style lang="scss" scoped>
.cc-report-container {
  padding: $card-padding;
  height: 100%;
  overflow: hidden;
}

/* Main Layout with Flexbox */
.cc-main-layout {
  display: flex;
  gap: $spacing-5;
  height: 100%;
  min-height: 700px; /* Ensure enough space for detail card */
  transition: all 0.3s ease;
  position: relative;
}

/* Detail Card Container (Left Side) */
.detail-card-container {
  position: absolute;
  left: 0;
  width: 340px;
  animation: slideInLeft 0.3s ease;
  transition: top 0.3s ease;
}

@keyframes slideInLeft {
  from {
    opacity: 0;
    transform: translateX(-20px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
}

/* Report Content Wrapper (Right Side) */
.report-content-wrapper {
  flex: 1;
  min-width: 0;
  min-height: 700px; /* Match main layout min-height */
  display: flex;
  flex-direction: column;
  transition: margin-left 0.3s ease;
}

/* Add left margin when detail card is open */
.cc-main-layout.has-detail-card .report-content-wrapper {
  margin-left: 360px;
}

.cc-header {
  margin-bottom: 20px;
  flex-shrink: 0;
}

.cc-header-title {
  @include typography-h3;
  @include font-weight-bold;
}

.cc-loading {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 200px;
}

.cc-error {
  color: red;
  padding: $card-padding;
  text-align: center;
}

.cc-report-content {
  background: white;
  border-radius: $radius-sm;
  box-shadow: 0 2px 4px rgba(black, 0.1);
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 600px; /* Ensure minimum height for grid container */
  overflow: hidden;
}

.multiline-cell {
  white-space: normal;
  line-height: 1.5;
  min-height: 24px;
}

/* Bound the grid to the viewport height so it scrolls INTERNALLY instead of
   growing unbounded and letting the page scroll. Once the grid has a bounded
   height, Kendo keeps the column header fixed while the rows scroll beneath
   it — a reliable, host-independent "frozen header".
   `--cc-grid-offset` is the vertical space above the grid (app header, page
   toolbar, this report's own toolbar/margins). Tune it if the header is
   clipped or leaves a gap. */
:deep(.k-grid) {
  --cc-grid-offset: 220px;
  flex: 1;
  min-height: 0;
  max-height: calc(100vh - var(--cc-grid-offset));
}

/* Ensure the data area is the scroller and the header stays put */
:deep(.k-grid-header) {
  flex: 0 0 auto;
}

:deep(.k-grid-content) {
  min-height: 0;
  overflow-y: auto;
}

:deep(.k-grid td) {
  vertical-align: top;
  padding: $spacing-2 $spacing-3;
}

/* Grid header styling - allow text wrapping for long column names */
:deep(.k-grid th) {
  white-space: normal !important;
  word-wrap: break-word;
  padding: $spacing-3 $spacing-2;
  line-height: 1.3;
  vertical-align: middle;
}

:deep(.k-grid .k-header) {
  white-space: normal !important;
  word-wrap: break-word;
}

/* Center align headers and cells */
:deep(.k-grid th.centered-header),
:deep(.k-grid th.centered-header .k-cell-inner),
:deep(.k-grid th.centered-header .k-link) {
  text-align: center !important;
  justify-content: center;
}

:deep(.k-grid td.centered-cell) {
  text-align: center !important;
}

/* Expand/collapse styling */
.expand-cell {
  cursor: pointer;
  text-align: center;
  user-select: none;
}

.expand-icon {
  display: inline-block;
  width: 20px;
  height: 20px;
  line-height: 18px;
  text-align: center;
  border: 1px solid #cccccc;
  border-radius: $radius-sm;
  @include typography-small;
  @include font-weight-bold;
  background-color: var(--vt-c-white-soft);
}

.expand-icon:hover {
  background-color: #e0e0e0;
  border-color: #999999;
}

.child-row-cell {
  background-color: var(--vt-c-white-soft);
}

:deep(.k-grid tr:has(.child-row-cell)) {
  background-color: var(--vt-c-white-soft);
}

/* Report Toolbar */
.report-toolbar {
  padding: 1rem;
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: $spacing-2;
  border-bottom: 1px solid #e0e0e0;
}

.expand-all-button {
  display: inline-flex;
  align-items: center;
  gap: $spacing-1;
}

/* Filter Flyout */
.filter-button-container {
  position: relative;
}

.filter-flyout {
  position: fixed;
  right: 0;
  top: 0;
  width: 400px;
  background: white;
  border-left: 1px solid #e0e0e0;
  box-shadow: -2px 0 8px rgba(black, 0.1);
  z-index: $z-notification;
  display: none;
  height: 100vh;
  overflow-y: auto;
}

.filter-flyout-open {
  display: block !important;
}

.filter-flyout-header {
  padding: 1rem;
  border-bottom: 1px solid #e0e0e0;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.filter-flyout-subheader h4 {
  @include typography-h4;
  @include font-weight-semibold;
  margin: 0 0 0.5rem 0;
}

.filter-flyout-subheader div {
  @include typography-small;
  color: #666;
}

.filter-flyout-content {
  padding: 1rem;
}

.no-filters-message {
  padding: 2rem 1rem;
  text-align: center;
  color: #666;
  font-style: italic;
}

.filter-flyout-footer {
  padding: 1rem;
  border-top: 1px solid #e0e0e0;
  display: flex;
  justify-content: flex-end;
  gap: 0.5rem;
}

.filter-group {
  margin-bottom: 1rem;
}

.filter-group label {
  @include font-weight-medium;
  display: block;
  margin-bottom: 0.5rem;
}

.filter-group select {
  width: 100%;
  padding: 0.5rem;
  border: 1px solid #cccccc;
  border-radius: $radius-sm;
}

.date-filter-container {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.date-filter-type {
  width: 100%;
}

.custom-date-range {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  margin-top: 0.5rem;
}

.date-input {
  width: 100%;
  padding: 0.5rem;
  border: 1px solid #cccccc;
  @include typography-small;
  border-radius: $radius-sm;
}

.k-button.k-primary {
  background-color: var(--vt-c-teal-medium);
  color: white;
}

.k-button.k-primary:hover {
  background-color: var(--vt-c-teal-dark);
}

/* Consultant Info Cell - Clickable */
.consultant-info-cell {
  cursor: pointer !important;
  color: var(--vt-c-teal-medium);
  transition: all 0.2s ease;
  user-select: none;
}

.consultant-info-cell:hover {
  background-color: #f0f8ff !important;
  color: var(--vt-c-teal-dark) !important;
}

:deep(.consultant-info-cell) {
  cursor: pointer !important;
  color: var(--vt-c-teal-medium) !important;
}

:deep(.consultant-info-cell:hover) {
  cursor: pointer !important;
  background-color: #f0f8ff !important;
  color: var(--vt-c-teal-dark) !important;
}

/* Responsive Design */
@media (max-width: 1200px) {
  .detail-card-container {
    width: 320px;
  }

  .cc-main-layout.has-detail-card .report-content-wrapper {
    margin-left: 340px;
  }
}

@media (max-width: 992px) {
  .detail-card-container {
    position: relative;
    width: 100%;
    max-height: 400px;
    top: 0 !important;
  }

  .cc-main-layout.has-detail-card .report-content-wrapper {
    margin-left: 0;
    margin-top: 20px;
  }

  .cc-main-layout {
    flex-direction: column;
    min-height: auto; /* Remove min-height on mobile */
  }

  .report-content-wrapper {
    min-height: auto; /* Remove min-height on mobile */
  }
}

@media (max-width: 768px) {
  .cc-report-container {
    padding: $spacing-2-5;
  }

  .detail-card-container {
    max-height: 350px;
  }
}
</style>

<style lang="scss">
.cc-report-container .expand-icon {
  padding-left:25%;
}

// Floating scrollbar — fixed to viewport bottom when grid extends below fold
.cc-report-container .grid-scroll-wrapper {
  position: relative;
}

.cc-report-container .floating-scrollbar {
  position: fixed;
  bottom: 0;
  z-index: $z-sticky;
  overflow-x: auto;
  overflow-y: hidden;
  height: 20px;
  background: var(--vt-c-white-soft, #f9f9f9);
  border-top: 1px solid var(--color-border, #e0e0e0);
  box-shadow: 0 -2px 4px rgba(0, 0, 0, 0.1);
}

.cc-report-container .floating-scrollbar-inner {
  height: 1px;
}
</style>
